import React from 'react';
import { Grid, Typography, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import FormLabelWithIcon from '../../Inputs/FormLabelWithIcon';
import {
  slidingToggleButtonNoBorder,
  slidingToggleGroupNoBorder
} from '../../../../SlidingToggleButton/noBorderStyles';
import { discFull } from '../../../../../constants/disc';

const useStyles = makeStyles()(theme => ({
  toggleGroup: {
    position: 'relative',
    backgroundColor: '#f3f5fb',
    borderRadius: '16px !important',
    padding: 2,
    zIndex: 0,
    border: '1px solid #e2e8f0',
    ...slidingToggleGroupNoBorder,
    [theme.breakpoints.down('sm')]: {
      width: '100%'
    }
  },
  toggleButton: {
    ...slidingToggleButtonNoBorder,
    borderRadius: '14px !important',
    padding: '0 10px',
    fontWeight: 'normal',
    fontSize: 12,
    height: 28,
    minWidth: 80,
    color: '#616161',
    textTransform: 'none',
    zIndex: 2,
    backgroundColor: 'transparent',
    transition: 'color 0.25s ease !important',
    WebkitTapHighlightColor: 'transparent',
    '&:hover': {
      backgroundColor: 'rgba(57, 102, 248, 0.04)',
      border: 'none !important',
      borderColor: 'transparent !important',
      boxShadow: 'none'
    },
    [theme.breakpoints.down('sm')]: {
      minWidth: 60,
      padding: '0 4px',
      fontSize: 11,
      flex: 1
    }
  },
  selected: {
    color: '#ffffff !important',
    backgroundColor: 'transparent !important',
    '&:hover': {
      backgroundColor: 'transparent !important'
    }
  },
  slidingIndicator: {
    position: 'absolute',
    top: 2,
    bottom: 2,
    left: 2,
    width: 'calc(50% - 2px)',
    backgroundColor: '#3966f8',
    borderRadius: 14,
    boxShadow: '0 2px 6px rgba(57, 102, 248, 0.2)',
    transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s ease, visibility 0.2s ease',
    zIndex: 1
  }
}));

const DISCPicker = ({ intl, traits = {}, onToggleTrait, required, personality, labelText }) => {
  const { classes } = useStyles();

  const group1Value = traits.outgoing ? 'outgoing' : traits.introverted ? 'introverted' : '';
  const group1Index = traits.outgoing ? 0 : traits.introverted ? 1 : -1;

  const group2Value = traits.logical ? 'logical' : traits.emotional ? 'emotional' : '';
  const group2Index = traits.logical ? 0 : traits.emotional ? 1 : -1;

  const handleToggleGroup1 = (event, val) => {
    if (val === null) {
      const current = traits.outgoing ? 'outgoing' : 'introverted';
      onToggleTrait(current);
    } else {
      onToggleTrait(val);
    }
  };

  const handleToggleGroup2 = (event, val) => {
    if (val === null) {
      const current = traits.logical ? 'logical' : 'emotional';
      onToggleTrait(current);
    } else {
      onToggleTrait(val);
    }
  };

  return (
    <Grid>
      <Grid container spacing={1} sx={{ flexDirection: 'column' }}>
        <Grid>
          <FormLabelWithIcon
            iconName="Brain"
            labelText={labelText || intl.formatMessage({ id: 'choose-personality' })}
            type="orange"
            required={required}
          />
          <Grid container spacing={2}>
            <Grid size={6}>
              <Grid container sx={{ justifyContent: 'center' }}>
                <ToggleButtonGroup
                  value={group1Value}
                  exclusive
                  onChange={handleToggleGroup1}
                  classes={{ root: classes.toggleGroup }}
                >
                  <div
                    className={classes.slidingIndicator}
                    style={{
                      transform: `translateX(${group1Index * 100}%)`,
                      opacity: group1Index === -1 ? 0 : 1,
                      visibility: group1Index === -1 ? 'hidden' : 'visible'
                    }}
                  />
                  <ToggleButton value="outgoing" classes={{ root: classes.toggleButton, selected: classes.selected }}>
                    {intl.formatMessage({ id: 'outgoing' })}
                  </ToggleButton>
                  <ToggleButton
                    value="introverted"
                    classes={{ root: classes.toggleButton, selected: classes.selected }}
                  >
                    {intl.formatMessage({ id: 'introverted' })}
                  </ToggleButton>
                </ToggleButtonGroup>
              </Grid>
            </Grid>
            <Grid size={6}>
              <Grid container sx={{ justifyContent: 'center' }}>
                <ToggleButtonGroup
                  value={group2Value}
                  exclusive
                  onChange={handleToggleGroup2}
                  classes={{ root: classes.toggleGroup }}
                >
                  <div
                    className={classes.slidingIndicator}
                    style={{
                      transform: `translateX(${group2Index * 100}%)`,
                      opacity: group2Index === -1 ? 0 : 1,
                      visibility: group2Index === -1 ? 'hidden' : 'visible'
                    }}
                  />
                  <ToggleButton value="logical" classes={{ root: classes.toggleButton, selected: classes.selected }}>
                    {intl.formatMessage({ id: 'logical' })}
                  </ToggleButton>
                  <ToggleButton value="emotional" classes={{ root: classes.toggleButton, selected: classes.selected }}>
                    {intl.formatMessage({ id: 'emotional' })}
                  </ToggleButton>
                </ToggleButtonGroup>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Grid>
          <Typography>
            {personality
              ? intl.formatMessage(
                  { id: 'chat-gpt-ice-breaking-drawer-content-with-disc' },
                  { personality: discFull(personality, intl.locale) }
                )
              : intl.formatMessage({ id: 'chat-gpt-ice-breaking-drawer-content-no-disc' })}
          </Typography>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default DISCPicker;
